<script setup lang="ts">
import { gsap } from 'gsap'

const localePath = useLocalePath()
const switchLocalePath = useSwitchLocalePath()
const { localeProperties, locale, locales } = useI18n()
const ctaSection = ref<HTMLElement | null>(null)
const dirX = useDirX()

const footerLanguages = computed(() => [[
  ...locales.value
    .filter(l => l.code !== locale.value)
    .map(lang => ({
      label: lang.name ?? lang.code,
      click: () => { window.location.href = switchLocalePath(lang.code) },
    })),
]])

onMounted(() => {
  const el = ctaSection.value
  if (!el) return

  const dir = dirX.value
  const isRTL = localeProperties.value.dir === 'rtl'

  // Stagger reveal — split by word in RTL (Arabic letters connect; per-char
  // splitting breaks ligatures), by character in LTR for the punchier effect.
  const heading = el.querySelector('.cta-heading')
  if (heading) {
    const text = (heading.textContent ?? '').trim()
    const parts = isRTL ? text.split(/(\s+)/) : text.split('')

    heading.innerHTML = parts.map((part) => {
      if (!part) return ''
      if (/^\s+$/.test(part)) return ' '
      return `<span class="cta-char inline-block" style="opacity:0;transform:translateY(60px) rotate(10deg)">${part}</span>`
    }).join('')

    gsap.to(el.querySelectorAll('.cta-char'), {
      opacity: 1, y: 0, rotation: 0, duration: 0.6, ease: 'back.out(1.8)', stagger: 0.04,
      scrollTrigger: { trigger: el, start: 'top 82%', once: true },
    })
  }

  // Arrow bounce + rotate
  gsap.fromTo(el.querySelector('.cta-arrow'),
    { opacity: 0, scale: 0.3, rotation: -90 },
    {
      opacity: 1, scale: 1, rotation: 0, duration: 0.85, ease: 'back.out(2)',
      scrollTrigger: { trigger: el, start: 'top 82%', once: true },
    },
  )

  // CTA card flies in from logical end
  gsap.fromTo(el.querySelector('.cta-card'),
    { opacity: 0, x: 100 * dir, rotation: 6, scale: 0.88 },
    {
      opacity: 1, x: 0, rotation: 0, scale: 1, duration: 1, ease: 'power4.out',
      scrollTrigger: { trigger: el, start: 'top 82%', once: true },
    },
  )

  // No-payment badge pop — rotation is mirrored in RTL so the badge tilts
  // outward (toward the card's nearest corner) in both reading directions.
  gsap.fromTo(el.querySelector('.cta-no-payment-badge'),
    { opacity: 0, scale: 0.3, rotation: -30 * dir },
    {
      opacity: 1, scale: 1, rotation: 6 * dir, duration: 0.75, ease: 'back.out(2.5)', delay: 0.5,
      scrollTrigger: { trigger: el, start: 'top 82%', once: true },
    },
  )

  // Footer nav links stagger
  gsap.fromTo(el.querySelectorAll('.footer-nav-link'),
    { opacity: 0, y: 16 },
    {
      opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', stagger: 0.07,
      scrollTrigger: { trigger: el.querySelector('.footer-nav'), start: 'top 95%', once: true },
    },
  )

  // Social icons stagger with pop
  gsap.fromTo(el.querySelectorAll('.footer-social'),
    { opacity: 0, scale: 0.5 },
    {
      opacity: 1, scale: 1, duration: 0.45, ease: 'back.out(2)', stagger: 0.08,
      scrollTrigger: { trigger: el.querySelector('.footer-socials'), start: 'top 95%', once: true },
    },
  )
})
</script>

<template>
  <div ref="ctaSection" class="min-h-screen bg-light-breeze flex flex-col justify-between gap-10 pb-10 pt-20">
    <!-- ── Final CTA ───────────────────────────────────── -->
    <section class="flex flex-col justify-center">
      <div class="container flex-1 flex flex-col justify-center">
        <div class="flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-6 pb-16 lg:pb-20">

          <!-- WHY WAIT? -->
          <div class="text-center lg:text-start shrink-0 lg:max-w-xl">
            <h4 class="cta-heading text-[clamp(5rem,11vw,9rem)] font-black text-gray-900 leading-none tracking-tight pb-5">
              {{ $t('ctaFooter.cta_heading_1') }}
            </h4>
            <h4 class="cta-heading text-[clamp(5rem,11vw,9rem)] font-black text-gray-900 leading-none tracking-tight pb-5">
              {{ $t('ctaFooter.cta_heading_2') }}
            </h4>
          </div>

          <!-- Arrow illustration -->
          <div class="hidden lg:flex items-center justify-center shrink-0 -mt-40 rotate-45">
            <img src="/illustrations/arrow.svg" alt="" class="cta-arrow w-20 h-20 ltr:-scale-x-100 ltr:-rotate-90" aria-hidden="true">
          </div>

          <!-- CTA Card -->
          <div class="cta-card relative bg-white border-2 border-gray-900 shadow-[8px_8px_0_#111827] rounded-2xl p-8 md:p-10 text-center max-w-md w-full mx-auto lg:mx-0">
            <!-- No Payment Required badge -->
            <div class="cta-no-payment-badge absolute -top-5 ltr:-right-2 rtl:-left-2 md:ltr:-right-6 md:rtl:-left-6 bg-creamy-yellow border-2 border-gray-900 rounded-md px-3.5 py-2 text-sm font-bold text-gray-900 shadow-[3px_3px_0_#111827] whitespace-nowrap" style="opacity:0">
              {{ $t('ctaFooter.no_payment_badge') }}
            </div>

            <h3 class="text-[clamp(1.75rem,3vw,2.5rem)] font-black text-gray-900 leading-tight mb-8 underline decoration-[3px] underline-offset-4">
              {{ $t('ctaFooter.cta_subheading') }}
            </h3>

            <a v-magnetic :href="localePath('/request-demo')" class="bfm-btn bfm-btn-lg bg-sky-blue">
              {{ $t('ctaFooter.request_demo') }}
            </a>
          </div>
        </div>
      </div>
    </section>

    <CommonWavyLine class="!block w-full" length="100%" :wl="40" :amp="10" :th="4" color="#1E90FF" :speed="1" />

    <!-- ── Footer ─────────────────────────────────────── -->
    <footer>
      <div class="container">
        <div class="flex flex-wrap items-start justify-between gap-8 pb-8 mb-6">

          <!-- Logo + tagline -->
          <div class="flex flex-col gap-3">
            <NuxtLink :to="localePath('/')">
              <img src="/h-logo.svg" alt="bookforme" class="h-10 w-auto">
            </NuxtLink>
            <p class="text-sm text-gray-500 uppercase leading-relaxed tracking-wide max-w-[200px]">
              {{ $t('ctaFooter.tagline') }}
            </p>
            <UDropdown :items="footerLanguages" :ui="{ rounded: 'rounded-xl', item: { rounded: 'rounded-lg', padding: 'px-3 py-2' } }">
              <button class="flex items-center gap-2 h-9 px-3.5 bg-white text-gray-900 text-sm font-bold rounded-lg border-2 border-gray-900 transition-colors hover:bg-gray-50">
                <UIcon name="i-heroicons-language-20-solid" class="size-4 shrink-0" />
                <span>{{ localeProperties.name }}</span>
              </button>
            </UDropdown>
          </div>

          <div>
            <!-- Nav links -->
            <nav class="footer-nav flex flex-wrap items-center gap-3 self-center" aria-label="Footer navigation">
              <NuxtLink
                :to="localePath('/faqs')"
                active-class="" exact-active-class=""
                class="footer-nav-link inline-flex items-center bg-white border-2 border-gray-900 rounded-lg px-4 py-2 text-sm font-bold text-gray-900 transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#111827]">{{ $t('ctaFooter.nav_faqs') }}</NuxtLink>
              <NuxtLink
                :to="localePath('/privacy-policy')"
                active-class="" exact-active-class=""
                class="footer-nav-link inline-flex items-center bg-white border-2 border-gray-900 rounded-lg px-4 py-2 text-sm font-bold text-gray-900 transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#111827]">{{ $t('ctaFooter.nav_privacy') }}</NuxtLink>
              <NuxtLink
                :to="localePath('/contact-us')"
                active-class="" exact-active-class=""
                class="footer-nav-link inline-flex items-center bg-white border-2 border-gray-900 rounded-lg px-4 py-2 text-sm font-bold text-gray-900 transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#111827]">{{ $t('ctaFooter.nav_contact') }}</NuxtLink>
              <NuxtLink
                :to="localePath('/contact-sales')"
                active-class="" exact-active-class=""
                class="footer-nav-link inline-flex items-center bg-white border-2 border-gray-900 rounded-lg px-4 py-2 text-sm font-bold text-gray-900 transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#111827]">{{ $t('ctaFooter.nav_contact_sales') }}</NuxtLink>
            </nav>

            <!-- Social icons -->
            <div class="footer-socials flex items-center gap-3 justify-start lg:justify-end mt-5" aria-label="Social media links">
              <a href="https://www.instagram.com/bookformehq/" target="_blank" rel="noopener noreferrer" :aria-label="$t('ctaFooter.social_instagram')" class="footer-social flex items-center justify-center w-11 h-11 rounded border-2 border-gray-900 bg-[#D6006E] text-white transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#111827]">
                <UIcon name="i-approved-instagram" class="size-6 text-white" />
              </a>
              <a href="https://www.facebook.com/profile.php?id=61576779412524" target="_blank" rel="noopener noreferrer" :aria-label="$t('ctaFooter.social_facebook')" class="footer-social flex items-center justify-center w-11 h-11 rounded border-2 border-gray-900 bg-[#1877F2] text-white transition-all hover:-translate-x-0.5 hover:-translate-y-0.5 hover:shadow-[3px_3px_0_#111827]">
                <UIcon name="i-approved-facebook" class="size-6 text-white" />
              </a>
            </div>
          </div>
        </div>

        <!-- Copyright -->
        <p class="text-center text-sm text-gray-500">
          Copyright © {{ new Date().getFullYear() }} - Owned by <a href="https://approved.tech/" target="_blank" rel="noopener noreferrer" class="underline hover:text-sky-blue">Approved Technologies W.L.L.</a>
        </p>
      </div>
    </footer>
  </div>
</template>
